import { ArrowDown, ArrowRight, ArrowUp, Clock, Sparkles } from 'lucide-react';
import { Badge, Button, Spinner, Track } from '../../../school_panel/components/ui';
import type { Blueprint, NextExamPrediction, PredictedChapter, PredictedTrend } from './types';
import { predictionPatch, yearsLabel } from './prediction';

interface Props {
  prediction: NextExamPrediction | undefined;
  loading: boolean;
  examName: string;
  patch: (p: Partial<Blueprint>) => void;
}

const TREND: Record<PredictedTrend, { label: string; icon: JSX.Element; cls: string }> = {
  up: { label: 'Rising', icon: <ArrowUp size={14} aria-hidden />, cls: 'text-success' },
  down: { label: 'Falling', icon: <ArrowDown size={14} aria-hidden />, cls: 'text-muted' },
  flat: { label: 'Steady', icon: <ArrowRight size={14} aria-hidden />, cls: 'text-muted' },
  due: { label: 'Due', icon: <Clock size={14} aria-hidden />, cls: 'text-accent-ink' },
};
const CONFIDENCE_TONE = { high: 'success', medium: 'info', low: 'neutral' } as const;

// Bars of the questions each past paper gave this chapter (oldest -> newest).
function History({ c }: { c: PredictedChapter }) {
  const max = Math.max(1, ...c.history.map(h => h.count));
  return (
    <span className="flex h-6 items-end gap-[3px]" aria-label={`Past papers: ${c.history.map(h => `${h.year ?? '?'} ${h.count}`).join(', ')}`} role="img">
      {c.history.map((h, i) => (
        <span key={i} title={`${h.year ?? 'Unknown year'}: ${h.count}`}
          className={`w-[7px] rounded-[2px] ${h.count ? 'bg-slate' : 'bg-track'}`}
          style={{ height: `${Math.max(12, (h.count / max) * 100)}%` }} />
      ))}
    </span>
  );
}

export function PredictionPanel({ prediction: p, loading, examName, patch }: Props) {
  if (loading || !p) {
    return <div className="flex items-center gap-2 py-4 text-sm text-muted"><Spinner size="sm" /> Reading past papers…</div>;
  }
  if (!p.available) {
    return <p className="py-2 text-sm text-warning-ink">No past {examName} papers for this subject yet, so there is nothing to predict from. Questions are spread evenly.</p>;
  }
  const tagged = p.tagging.total ? Math.round((100 * p.tagging.tagged) / p.tagging.total) : 100;
  return (
    <section aria-label={`Predicted next ${examName}`} className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-ink">Predicted next {examName}</p>
          <p className="mt-0.5 text-xs text-muted">
            From {p.papers} past {p.papers === 1 ? 'paper' : 'papers'}{yearsLabel(p.years) ? ` (${yearsLabel(p.years)})` : ''}; recent years count more.
            {p.backtest
              ? <> Predicting {p.backtest.year} from the years before it matched <strong className="text-ink">{p.backtest.match_pct}%</strong> of its chapter spread.</>
              : ' Only one year of papers, so treat this as a rough guide.'}
          </p>
        </div>
        <Button size="sm" icon={<Sparkles size={14} aria-hidden />} onClick={() => patch(predictionPatch(p))}>Use this prediction</Button>
      </div>

      {p.tagging.total > 0 && p.tagging.tagged < p.tagging.total && (
        <div className="rounded-[12px] bg-surface-2 px-3 py-2.5">
          <p className="text-xs text-muted">
            {p.tagging.running ? 'Analysing past questions for concepts and difficulty' : 'Some past questions are not analysed yet'}:
            {' '}{p.tagging.tagged} of {p.tagging.total}. Concepts and difficulty sharpen as this finishes.
          </p>
          <Track className="mt-1.5" value={tagged} tone="slate" label="Past questions analysed" />
        </div>
      )}

      {p.difficulty && (
        <p className="text-xs text-muted">
          Difficulty: <span className="text-ink">{p.difficulty.easy}% easy · {p.difficulty.medium}% medium · {p.difficulty.hard}% hard</span>
        </p>
      )}

      <ul className="divide-y divide-hairline rounded-[14px] border border-line" aria-label="Predicted chapters">
        {p.chapters.map(c => {
          const t = TREND[c.trend];
          return (
            <li key={c.chapter_id} className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1.5 px-3.5 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-ink">{c.name}</p>
                <p className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs">
                  <span className={`inline-flex items-center gap-1 ${t.cls}`}>{t.icon}{t.label}</span>
                  <Badge tone={CONFIDENCE_TONE[c.confidence]}>{c.confidence} confidence</Badge>
                  {c.difficulty && <span className="text-muted">{c.difficulty.hard}% hard</span>}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <History c={c} />
                <span className="w-12 text-right font-mono text-sm font-semibold text-ink" aria-label={`${c.predicted} predicted questions`}>{c.predicted} Q</span>
              </div>
              {c.concepts.length > 0 && (
                <ul className="col-span-2 flex flex-wrap gap-1.5" aria-label={`Likely concepts in ${c.name}`}>
                  {c.concepts.map(k => (
                    <li key={k.name} title={k.status === 'due' ? 'Examined before, not in the last two papers' : k.status === 'recurring' ? 'Examined in several papers' : 'Examined recently'}
                      className={`rounded-full px-2.5 py-0.5 text-xs ${k.status === 'due' ? 'bg-accent-bg text-accent-ink' : k.status === 'recurring' ? 'bg-mist text-ink' : 'bg-surface-2 text-muted-2'}`}>
                      {k.name}{k.status === 'due' ? ' · due' : ''}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
      <p className="text-xs text-muted">
        Using the prediction also tells the AI which concepts to cover in each chapter and which past questions not to copy.
      </p>
    </section>
  );
}
